import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { toast } from 'sonner';
import { 
  Building2, CheckCircle, User, Phone, Lock, Mail, 
  MapPin, ArrowRight, ArrowLeft, Loader2, School
} from 'lucide-react';

const SchoolSetupPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [schoolIdValidated, setSchoolIdValidated] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: School ID
    school_id: '',
    // Step 2: School Details
    school_name: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    // Step 3: Admin Account
    admin_name: '',
    admin_phone: '',
    admin_email: '',
    admin_password: '',
    confirm_password: ''
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { number: 1, title: 'Verify School ID' },
    { number: 2, title: 'School Details' },
    { number: 3, title: 'Admin Account' },
    { number: 4, title: 'Complete' }
  ];

  // Step 1: Validate School ID
  const validateSchoolId = async () => {
    if (!formData.school_id.trim()) {
      setErrors({ school_id: 'Please enter your School ID' });
      return;
    }

    // Basic format validation
    const pattern = /^[A-Z]+-[A-Z]{3}-\d{4}$/;
    if (!pattern.test(formData.school_id.toUpperCase())) {
      setErrors({ school_id: 'Invalid format. Expected: WORD-ABC-1234 (e.g., PILOT-GHY-1234)' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await apiClient.post('/v1/setup/validate-school-id', {
        school_id: formData.school_id.toUpperCase()
      });

      if (response.data.valid) {
        setFormData(prev => ({
          ...prev,
          school_id: formData.school_id.toUpperCase(),
          school_name: response.data.school_name || ''
        }));
        setSchoolIdValidated(true);
        setCurrentStep(2);
        toast.success('School ID verified successfully!');
      } else {
        setErrors({ school_id: response.data.message || 'Invalid School ID' });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to validate School ID';
      setErrors({ school_id: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Validate School Details
  const validateSchoolDetails = () => {
    const newErrors = {};

    if (!formData.school_name.trim()) {
      newErrors.school_name = 'School name is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }
    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Phone is required';
    }
    if (formData.contact_phone.trim().length !== 10) {
      newErrors.contact_phone = 'Enter a valid phone number';
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      setCurrentStep(3);
    }
  };

  // Step 3: Validate Admin Details and Submit
  const validateAndSubmit = async () => {
    const newErrors = {};

    if (!formData.admin_name.trim()) {
      newErrors.admin_name = 'Name is required';
    }
    if (!formData.admin_phone.trim()) {
      newErrors.admin_phone = 'Phone is required';
    }
    if (formData.admin_phone.trim().length !== 10) {
      newErrors.admin_phone = 'Enter a valid phone number';
    }
    if (!formData.admin_password) {
      newErrors.admin_password = 'Password is required';
    } else if (formData.admin_password.length < 6) {
      newErrors.admin_password = 'Password must be at least 6 characters';
    }
    if (formData.admin_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      // Check phone availability first
      const phoneCheck = await apiClient.get(`/v1/setup/check-phone/${formData.admin_phone}`);
      if (!phoneCheck.data.available) {
        setErrors({ admin_phone: 'Phone already registered. Please login instead.' });
        setLoading(false);
        return;
      }

      // Register school and admin
      const response = await apiClient.post('/v1/setup/register-school', {
        school_id: formData.school_id.toUpperCase(),
        school_name: formData.school_name,
        address: formData.address,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        admin_name: formData.admin_name,
        admin_phone: formData.admin_phone,
        admin_password: formData.admin_password,
        admin_email: formData.admin_email || null
      });

      if (response.data.success) {
        toast.success('School registered successfully!');
        setCurrentStep(4);
        
        // Auto redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { state: { role: 'admin', phone: formData.admin_phone } });
        }, 3000);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      
      if (message.includes('Phone already registered')) {
        setErrors({ admin_phone: message });
      } else if (message.includes('School ID is already registered')) {
        setCurrentStep(1);
        setSchoolIdValidated(false);
        setErrors({ school_id: message });
      }
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (currentStep > 1 && currentStep < 4) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #5A9FD4 100%)' }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#E8F4FD' }}>
            <School className="w-8 h-8" style={{ color: '#1E3A5F' }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#1E3A5F' }}>School Setup</h1>
          <p className="text-gray-600 mt-1">Register your school on DormCircle</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                    currentStep > step.number 
                      ? 'bg-green-500 text-white' 
                      : currentStep === step.number 
                        ? 'text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}
                  style={currentStep === step.number ? { backgroundColor: '#1E3A5F' } : {}}
                >
                  {currentStep > step.number ? <CheckCircle className="w-5 h-5" /> : step.number}
                </div>
                <span className={`text-xs mt-1 ${currentStep >= step.number ? 'text-gray-700' : 'text-gray-400'}`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: School ID Verification */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">School ID</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  data-testid="school-id-input"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg text-lg font-mono uppercase ${errors.school_id ? 'border-red-500' : ''}`}
                  placeholder="e.g., PILOT-ABC-1234"
                  value={formData.school_id}
                  onChange={(e) => handleInputChange('school_id', e.target.value.toUpperCase())}
                />
              </div>
              {errors.school_id && <p className="text-red-500 text-sm mt-1">{errors.school_id}</p>}
              <p className="text-sm text-gray-500 mt-2">
                Enter the School ID provided by your institution. Format: WORD-ABC-1234
              </p>
            </div>

            <button
              data-testid="validate-btn"
              onClick={validateSchoolId}
              disabled={loading}
              className="w-full py-3 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#1E3A5F' }}
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Validating...</>
              ) : (
                <>Verify & Continue <ArrowRight className="w-5 h-5" /></>
              )}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-sm hover:underline" style={{ color: '#5A9FD4' }}>
                Already registered? Login here
              </Link>
            </div>
          </div>
        )}

        {/* Step 2: School Details */}
        {currentStep === 2 && (
          <div className="space-y-4">
            {/* School ID Badge */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm text-green-600">School ID Verified</p>
                <p className="font-mono text-green-800">{formData.school_id}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">School Name *</label>
              <div className="relative">
                <School className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  data-testid="school-name-input"
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.school_name ? 'border-red-500' : ''}`}
                  placeholder="School Name"
                  value={formData.school_name}
                  onChange={(e) => handleInputChange('school_name', e.target.value)}
                />
              </div>
              {errors.school_name && <p className="text-red-500 text-sm mt-1">{errors.school_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  data-testid="address-input"
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.address ? 'border-red-500' : ''}`}
                  rows="2"
                  placeholder="Full school address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
              {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    data-testid="contact-email-input"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.contact_email ? 'border-red-500' : ''}`}
                    placeholder="school@example.com"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  />
                </div>
                {errors.contact_email && <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    data-testid="contact-phone-input"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.contact_phone ? 'border-red-500' : ''}`}
                    placeholder="9876543210"
                    minLength={10}
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  />
                </div>
                {errors.contact_phone && <p className="text-red-500 text-sm mt-1">{errors.contact_phone}</p>}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={goBack}
                className="flex-1 px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                data-testid="continue-step2-btn"
                onClick={validateSchoolDetails}
                className="flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2"
                style={{ backgroundColor: '#1E3A5F' }}
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Admin Account */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Setting up:</strong> {formData.school_name}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Full Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  data-testid="admin-name-input"
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.admin_name ? 'border-red-500' : ''}`}
                  placeholder="Your full name"
                  value={formData.admin_name}
                  onChange={(e) => handleInputChange('admin_name', e.target.value)}
                />
              </div>
              {errors.admin_name && <p className="text-red-500 text-sm mt-1">{errors.admin_name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    data-testid="admin-phone-input"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.admin_phone ? 'border-red-500' : ''}`}
                    placeholder="9876543210"
                    value={formData.admin_phone}
                    onChange={(e) => handleInputChange('admin_phone', e.target.value)}
                  />
                </div>
                {errors.admin_phone && <p className="text-red-500 text-sm mt-1">{errors.admin_phone}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    data-testid="admin-email-input"
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    placeholder="admin@school.com"
                    value={formData.admin_email}
                    onChange={(e) => handleInputChange('admin_email', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    data-testid="admin-password-input"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.admin_password ? 'border-red-500' : ''}`}
                    placeholder="Min 6 characters"
                    value={formData.admin_password}
                    onChange={(e) => handleInputChange('admin_password', e.target.value)}
                  />
                </div>
                {errors.admin_password && <p className="text-red-500 text-sm mt-1">{errors.admin_password}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    data-testid="confirm-password-input"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg ${errors.confirm_password ? 'border-red-500' : ''}`}
                    placeholder="Confirm password"
                    value={formData.confirm_password}
                    onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                  />
                </div>
                {errors.confirm_password && <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={goBack}
                className="flex-1 px-4 py-2 border text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                data-testid="register-btn"
                onClick={validateAndSubmit}
                disabled={loading}
                className="flex-1 px-4 py-2 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#1E3A5F' }}
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Registering...</>
                ) : (
                  <>Complete Setup <CheckCircle className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {currentStep === 4 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 bg-green-100">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Setup Complete!</h2>
            <p className="text-gray-600 mb-6">
              Your school <strong>{formData.school_name}</strong> has been registered successfully.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">Login Credentials:</p>
              <p className="font-mono text-gray-800">Phone: {formData.admin_phone}</p>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Redirecting to login page in a few seconds...
            </p>
            <button
              onClick={() => navigate('/login', { state: { role: 'admin', phone: formData.admin_phone } })}
              className="px-6 py-2 text-white rounded-lg"
              style={{ backgroundColor: '#1E3A5F' }}
            >
              Go to Login Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolSetupPage;
